// @ts-strict-ignore
import { createSelector } from 'reselect';
import type {
  Bounds,
  Graph,
} from 'flow/service/graph';
import {
  getAllNodeIdsConnectedToNode,
  getMaxPathLengthMap,
  getBidirectionalGraphTopology,
  layOutGraph,
  adjustConstructionAndFixedNodes,
  appendAdditionalGraphElements,
  buildGraph,
  shouldAddSkeletonNodeAndEdge,
  organizeContainerNodes,
  countNodesByType,
  getFolderSize,
  filterNodesByLineage,
  filterEdgesByLineage,
  filterOutUnsetTagNodes,
  filterOutUnsetTagEdges,
  appendFolderNode,
  buildSnippetSubgraphs,
  buildSnippetSubgraphTopolgies,
  buildSnippetSubgraphMaxPathMaps,
  buildSnippetSubgraphIncomersMap,
  adjustSnippetChildIncomers,
  adjustEdgesForSnippetNodes,
  buildSubgraphDimensions,
  FOLDER_NODE_ID,
  getGraphBounds,
  getCanvasTranslateExtent,
  isGraphRequiredSpaceLargerThanCanvas,
  getExternalToInternalSnippetEdges,
} from 'flow/service/graph';
import type { State } from '~/store';
import {
  selectDiffMode,
  selectIsComplexOutcomeIslandOpen,
  selectIsEditingLinkedChildAction,
  selectIsNodeEditorDrawerOpen,
  selectNodeConnectorState,
  selectWorkflowData,
  selectFeatureFlagsForStore,
} from './general';
import {
  selectNodeContainerState,
  selectSnippetCollapsedStates,
  selectNodeFolderState,
  selectShouldRenderCriticalNodePath,
} from './ui';
import type { PublicId } from 'flow/store/engine/types';
import { defaultNodeDimensions } from 'flow/service/graph/layout/nodeMatrix';
import { selectCanvasRect, selectCanvasSize } from './canvas';
import { getSimpleIslandMode } from 'flow/service/graph/layout/helpers';
import { selectInitialWorkflowData } from './history';
import { mergeDependencies, mergeUniqueValues } from '../helpers';
import buildDependencies from 'flow/store/engine/dependencies';


export const selectGraph = createSelector(
  [
    selectInitialWorkflowData,
    selectWorkflowData,
    (state: State) => state.workflowEditor.workflowProps,
    selectDiffMode,
    selectFeatureFlagsForStore,
  ],
  (initialData, currentData, workflowProps, isInDiffMode, featureFlagsForStore) => {
    if (featureFlagsForStore?.enableWorkflowDiff && isInDiffMode) {
      const initialDependencies = buildDependencies({
        actions: initialData?.actions,
        thresholds: initialData?.thresholds,
        dataRoutes: initialData?.dataRoutes,
      });
      const currentDependencies = buildDependencies({
        actions: currentData?.actions,
        thresholds: currentData?.thresholds,
        dataRoutes: currentData?.dataRoutes,
      });
      const mergedActions = mergeUniqueValues({
        initial: initialData?.actions,
        current: currentData?.actions,
      });
      const mergedDependencies = mergeDependencies({
        initial: initialDependencies,
        current: currentDependencies,
      });
      return buildGraph(mergedActions, mergedDependencies, workflowProps);
    }
    return buildGraph(currentData?.actions, currentData?.dependencies, workflowProps);
  },
);

export const selectHasNodeContainers = createSelector(
  [
    selectGraph,
  ],
  graph => {
    const {
      inputAttribute: inputAttributeCount,
      snippet: snippetCount,
    } = countNodesByType(graph.nodes);

    return { hasNodeFolder: inputAttributeCount > 1, hasSnippets: snippetCount > 0 };
  },
);

/** Appends the graph with additional elements dependent on the state of the graph. */
const selectGraphWithAdditionalElements = createSelector(
  [
    selectGraph,
    selectNodeContainerState,
    selectHasNodeContainers,
  ],
  (
    _graph,
    nodeContainerState,
    hasNodeContainers,
  ) => {
    const { hasNodeFolder, hasSnippets } = hasNodeContainers;

    const graph = hasNodeFolder ? appendFolderNode(structuredClone(_graph)) : _graph;

    return (hasNodeFolder || hasSnippets)
      ? organizeContainerNodes(structuredClone(graph), nodeContainerState)
      : graph;
  },
);

const selectSnippetVirtualEdges = createSelector(
  [
    selectGraphWithAdditionalElements,
    selectHasNodeContainers,
  ],
  (
    graph,
    hasNodeContainers,
  ) => {
    const { hasSnippets } = hasNodeContainers;

    return hasSnippets
      ? getExternalToInternalSnippetEdges(graph)
      : [];
  },
);

/** Selects the set of edge ids in the graph, not including virtual edges */
const selectOriginalEdgeIdSet = createSelector(
  [
    selectGraphWithAdditionalElements,
  ],
  graph => new Set(graph.edges.map(edge => edge.id)),
);

/** Appends the graph with any ui-interaction dependent elements */
const selectGraphWithUiDependentElements = createSelector(
  [
    selectGraphWithAdditionalElements,
    selectSnippetVirtualEdges,
    selectNodeConnectorState,
    selectIsEditingLinkedChildAction,
  ],
  (
    graph,
    snippetVirtualEdges,
    nodeConnectorState,
    isEditingLinkedChildAction,
  ) => {
    const { ownerNodeId } = nodeConnectorState;
    const appendedGraph = structuredClone(graph);

    appendedGraph.edges.push(...snippetVirtualEdges);

    if (!shouldAddSkeletonNodeAndEdge(nodeConnectorState, isEditingLinkedChildAction)) {
      return appendedGraph;
    }

    return appendAdditionalGraphElements(
      structuredClone(appendedGraph),
      { ownerNodeId },
    );
  },
);

const selectLayOutDependentUIState = createSelector(
  [
    (state: State) => state.workflowEditor.ui.focusedNodeId,
    (state: State) => state.workflowEditor.ui.activeNodeEditorTab,
    (state: State) => state.workflowEditor.ui.floatedAnchorNodeId,
    (state: State) => state.workflowEditor.ui.canvasProperties.rect.width,
    (state: State) => state.workflowEditor.ui.highlightedNodeId,
    (state: State) => state.workflowEditor.ui.hoveredNodeId,
    selectNodeFolderState,
  ],
  (
    focusedNodeId,
    activeNodeEditorTab,
    floatedAnchorNodeId,
    canvasWidth,
    highlightedNodeId,
    hoveredNodeId,
    nodeFolder,
  ) => ({
    focusedNodeId,
    activeNodeEditorTab,
    floatedAnchorNodeId,
    canvasWidth,
    highlightedNodeId,
    hoveredNodeId,
    nodeFolder,
  }),
);

const selectAnchorNodeId = createSelector(
  [
    (state: State) => state.workflowEditor.ui.focusedNodeId,
    (state: State) => state.workflowEditor.ui.floatedAnchorNodeId,
  ], (focusedNodeId, floatedNodeId) => {
    if (focusedNodeId) return focusedNodeId;
    if (floatedNodeId) return floatedNodeId;
    return null;
  });

export const selectGraphTopology = createSelector(
  [
    selectGraphWithUiDependentElements,
    selectNodeContainerState,
  ], (
    graph,
    nodeContainerState,
  ) => getBidirectionalGraphTopology(graph, nodeContainerState),
);


export const selectFolderSize = createSelector(
  [
    selectGraphWithUiDependentElements,
    selectNodeFolderState,
  ],
  (graph, nodeFolderState) => getFolderSize(nodeFolderState, graph.nodes),
);

export const selectSnippetMaxPathLengths = createSelector(
  [
    selectGraph,
  ], graph => {
    const subgraphs = buildSnippetSubgraphs(graph);
    const topologies = buildSnippetSubgraphTopolgies(subgraphs);
    const incomers = buildSnippetSubgraphIncomersMap(topologies);

    return buildSnippetSubgraphMaxPathMaps(incomers, subgraphs);
  },
);

export const selectSnippetDimensions = createSelector(
  [
    selectSnippetMaxPathLengths,
    selectSnippetCollapsedStates,
  ], (snippetMaxPathLengths, snippetCollapsedStates) => (
    buildSubgraphDimensions(snippetMaxPathLengths, snippetCollapsedStates)
  ),
);

export const selectSubGraphDimensions = createSelector(
  [
    selectSnippetDimensions,
    selectNodeFolderState,
    selectFolderSize,
  ], (snippetDimensions, folderCollapsedState, folderSize) => {
    const { isCollapsed } = folderCollapsedState;
    const folderDimensions = isCollapsed
      ? defaultNodeDimensions
      : { nodeHeight: folderSize + 1, nodeWidth: 1 };

    return {
      ...snippetDimensions,
      [FOLDER_NODE_ID.inputAttribute]: folderDimensions,
    };
  },
);

export function selectSubgraphDimensionsById(publicId: PublicId) {
  return function selector(state: State) {
    const subGraphMaxPathMaps = selectSubGraphDimensions(state);
    return subGraphMaxPathMaps[publicId];
  };
}

const selectRenderPathIncomers = createSelector(
  [
    selectGraphWithUiDependentElements,
    selectGraphTopology,
  ],
  (graph, graphTopology) => (
    adjustSnippetChildIncomers({ graph, incomers: graphTopology.incomers })
  ),
);

const selectMaxIncomingPathLengthMap = createSelector(
  [
    selectRenderPathIncomers,
    selectGraphWithUiDependentElements,
    selectSubGraphDimensions,
  ],
  (incomers, graph, subGraphDimensions) => (
    getMaxPathLengthMap(
      {
        nodes: graph.nodes,
        incomers,
        subGraphDimensions,
      },
    )
  ),
);

export const selectNodeLineageForAnchorNode = createSelector(
  [
    selectGraphTopology,
    selectAnchorNodeId,
  ],
  (
    graphTopology,
    anchorNodeId,
  ) => getAllNodeIdsConnectedToNode(anchorNodeId, graphTopology),
);

export const selectUnsetTagNodeIds = createSelector([
  (state: State) => state.workflowEditor.workflowData?.actions,
  (state: State) => state.workflowEditor.workflowFeatures.ruleExplainability.setTagNames,
], (actions, setTagNames) => {
  if (actions && setTagNames) {
    return Object
      .values(actions)
      .reduce((acc, { publicId, tag }) => {
        if (tag && !setTagNames.includes(tag.name)) {
          acc.push(publicId);
        }
        return acc;
      }, []);
  }
  return [];
});

export const selectCriticalNodePathGraph = createSelector(
  [
    selectGraph,
    selectGraphTopology,
    selectShouldRenderCriticalNodePath,
    (state: State) => state.workflowEditor.ui.focusedNodeId,
    selectUnsetTagNodeIds,
  ],
  (
    _graph,
    graphTopology,
    shouldRenderCriticalNodePath,
    focusedNodeId,
    unsetTagNodeIds,
  ): Graph | null => {
    if (!shouldRenderCriticalNodePath) {
      return null;
    }

    const focusedTagNodeLineage = focusedNodeId
      ? getAllNodeIdsConnectedToNode(
        focusedNodeId,
        graphTopology,
      )
      : {
        nodeId: null,
        directIncomers: new Set<string>(),
        directOutgoers: new Set<string>(),
        extendedIncomers: new Set<string>(),
        extendedOutgoers: new Set<string>(),
        all: new Set(_graph.nodes.map(node => node.id)),
      };

    return {
      nodes: filterNodesByLineage(
        filterOutUnsetTagNodes(_graph.nodes, unsetTagNodeIds),
        focusedTagNodeLineage,
      ),
      edges: filterEdgesByLineage(
        filterOutUnsetTagEdges(_graph.edges, unsetTagNodeIds),
        focusedTagNodeLineage,
      ),
    };
  },
);

export const selectLayOutedGraph = createSelector(
  [
    selectGraphWithUiDependentElements,
    selectMaxIncomingPathLengthMap,
    selectSubGraphDimensions,
    selectNodeLineageForAnchorNode,
    selectLayOutDependentUIState,
    selectHasNodeContainers,
    selectCriticalNodePathGraph,
  ],
  (
    _graph,
    maxIncomingPathLengthMap,
    subGraphDimensions,
    nodeLineage,
    uiStatePartial,
    { hasNodeFolder },
    _criticalNodePathGraph,
  ) => {
    const graph = structuredClone(_criticalNodePathGraph || _graph);

    layOutGraph({
      graph,
      uiStatePartial,
      nodeLineage,
      maxIncomingPathLengthMap,
      subGraphDimensions,
      hasInputAttributeNodeFolder: hasNodeFolder,
    });

    return graph;
  });

const selectLayoutAdjustedGraph = createSelector(
  [
    selectLayOutedGraph,
    selectSnippetCollapsedStates,
    selectSnippetVirtualEdges,
    selectOriginalEdgeIdSet,
  ],
  (
    _graph,
    snippetCollapsedStates,
    snippetVirtualEdges,
    originalEdgeIdSet,
  ): Graph => {
    const graph = structuredClone(_graph);

    const edges = adjustEdgesForSnippetNodes({
      edges: graph.edges,
      snippetCollapsedStates,
      snippetVirtualEdges,
      originalEdgeIdSet,
    });

    return { nodes: graph.nodes, edges };
  },
);

export const selectGraphBounds = createSelector(
  [
    selectLayOutedGraph,
  ],
  (graph): Bounds => getGraphBounds(graph),
);

export const selectSimpleIslandMode = createSelector(
  [
    selectGraphBounds,
    selectCanvasRect,
  ],
  (graphBounds, canvasRect) => getSimpleIslandMode(graphBounds, canvasRect),
);

export const selectIsGraphLargerThanCanvas = createSelector(
  [
    selectGraphBounds,
    selectCanvasSize,
  ],
  /**
   * todo BUILD-2907: eventually, selecting the canvas translate extent and comparing it against
   * the canvas width should be enough and the invoked function in this selector could be deleted.
   */
  (graphBounds, canvasSize) => isGraphRequiredSpaceLargerThanCanvas(graphBounds, canvasSize),
);

export const selectCanvasTranslateExtent = createSelector(
  [
    selectCanvasSize,
    selectIsComplexOutcomeIslandOpen,
    selectIsNodeEditorDrawerOpen,
    selectGraphBounds,
  ],
  (canvasSize, isComplexIslandOpen, isNodeEditorDrawerOpen, graphBounds) => (
    getCanvasTranslateExtent({
      canvasSize,
      graphBounds,
      isComplexIslandOpen,
      isNodeEditorDrawerOpen,
    })
  ),
);

const selectCanvasAdjustedGraph = createSelector(
  [
    selectLayoutAdjustedGraph,
    selectSimpleIslandMode,
    (state: State) => state.workflowEditor.ui.canvasProperties.rect,
    (state: State) => state.workflowEditor.ui.focusedNodeId,
  ],
  (_graph, islandMode, canvasRect, focusedNodeId) => {
    const graph = structuredClone(_graph);

    adjustConstructionAndFixedNodes({
      graph,
      islandMode,
      canvasRect,
      focusedNodeId,
    });

    return graph;
  },
);

export const selectWorkflowGraph = createSelector(
  [
    selectCanvasAdjustedGraph,
  ],
  graph => graph,
);

export const selectFirstTagNodeId = createSelector(
  [
    selectWorkflowGraph,
  ],
  graph => {
    const tagNodes = graph.nodes
      .filter(node => node.type === 'tag')
      .sort((a, b) => {
        // Sort by visual position: first by x coordinate (left to right), then by y coordinate (top to bottom)
        if (a.position.x !== b.position.x) {
          return a.position.x - b.position.x;
        }
        return a.position.y - b.position.y;
      });

    return tagNodes.length > 0 ? tagNodes[0].id : null;
  },
);

export const selectIsWorkflowGraphEmpty = createSelector(
  [
    selectGraph,
  ],
  graph => graph.nodes.length === 0,
);
